/**
 * Jewelry motion — Framer Motion–like scroll reveals & micro-interactions
 * Pure JS + CSS. Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reduceMotionQuery =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduce = reduceMotionQuery && reduceMotionQuery.matches;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function markSections() {
    var selectors = [
      '.jw-trust',
      '.jw-service-finder',
      '.jw-collections',
      '.jw-promo',
      '.jw-story',
      '.jw-footer',
    ];

    document.querySelectorAll(selectors.join(',')).forEach(function (el, index) {
      if (el.closest('.jw-hero')) return;
      if (el.hasAttribute('data-jw-reveal')) return;
      el.setAttribute('data-jw-reveal', '');
      el.style.setProperty('--jw-stagger', String((index % 8) * 0.05) + 's');
    });

    // Stagger children in grids
    document.querySelectorAll('.jw-collections__grid, .jw-trust__grid, .jw-promo__steps').forEach(function (grid) {
      Array.prototype.forEach.call(grid.children, function (child, i) {
        if (child.hasAttribute('data-jw-reveal')) return;
        child.setAttribute('data-jw-reveal', '');
        child.style.setProperty('--jw-stagger', String(i * 0.07) + 's');
      });
    });
  }

  function observeReveals() {
    var nodes = document.querySelectorAll('[data-jw-reveal]');
    if (!nodes.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) {
        n.classList.add('is-inview');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            io.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    nodes.forEach(function (n) {
      io.observe(n);
    });
  }

  function enhanceHero() {
    var heroes = document.querySelectorAll('.jw-hero');
    heroes.forEach(function (root) {
      if (root.getAttribute('data-jw-motion-hero') === '1') return;
      root.setAttribute('data-jw-motion-hero', '1');
      root.classList.add('jw-hero--motion');
      var slides = Array.prototype.slice.call(root.querySelectorAll('.jw-hero__slide'));
      if (!slides.length) return;

      // Smooth slide transitions via classes
      var dots = Array.prototype.slice.call(root.querySelectorAll('.jw-hero__dot'));
      var toggle = root.querySelector('[data-jw-hero-toggle]');
      var slideContainer = root.querySelector('.jw-hero__slides');
      var i = 0;
      var timer;
      var userPaused = root.getAttribute('data-autoplay') !== 'true' || reduce;
      var interval = parseInt(root.getAttribute('data-interval') || '5500', 10);
      var locked = false;
      var hoverPaused = false;
      var focusPaused = false;

      function updateLiveRegion() {
        if (!slideContainer) return;
        var isRotating = !userPaused && !hoverPaused && !focusPaused && !document.hidden;
        slideContainer.setAttribute('aria-live', isRotating ? 'off' : 'polite');
      }

      function updateToggle() {
        if (!toggle) return;
        var state = userPaused ? 'paused' : 'playing';
        var label = userPaused
          ? toggle.getAttribute('data-play-label')
          : toggle.getAttribute('data-pause-label');
        toggle.setAttribute('data-state', state);
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('title', label);
        updateLiveRegion();
      }

      function loadSlideImage(index) {
        var image = slides[index] && slides[index].querySelector('img[data-src]');
        if (!image) return Promise.resolve();

        return new Promise(function (resolve) {
          var settled = false;
          var finishing = false;
          var timeout;

          function settle() {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            image.classList.remove('jw-hero__img--deferred');
            image.setAttribute('data-jw-loaded', 'true');
            resolve();
          }

          function finish() {
            if (settled || finishing) return;
            finishing = true;
            if (image.decode && image.naturalWidth > 0) {
              image.decode().catch(function () {}).then(settle);
            } else {
              settle();
            }
          }

          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', settle, { once: true });

          if (image.getAttribute('data-srcset')) {
            image.setAttribute('srcset', image.getAttribute('data-srcset'));
            image.removeAttribute('data-srcset');
          }
          if (image.getAttribute('data-sizes')) {
            image.setAttribute('sizes', image.getAttribute('data-sizes'));
            image.removeAttribute('data-sizes');
          }

          image.loading = 'eager';
          image.setAttribute('fetchpriority', 'high');
          image.src = image.getAttribute('data-src');
          image.removeAttribute('data-src');
          timeout = window.setTimeout(settle, 5000);

          if (image.complete) window.setTimeout(finish, 0);
        });
      }

      function updateAccessibility() {
        slides.forEach(function (slide, index) {
          var active = index === i;
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if ('inert' in slide) slide.inert = !active;
        });
        dots.forEach(function (dot, index) {
          if (index === i) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      function activateSlide(next) {
        var prev = i;
        slides[prev].classList.remove('is-active');
        slides[prev].classList.add('is-leaving');
        if (dots[prev]) dots[prev].classList.remove('is-active');

        i = next;
        slides[i].classList.add('is-active', 'is-entering');
        if (dots[i]) dots[i].classList.add('is-active');
        updateAccessibility();

        window.setTimeout(function () {
          slides[prev].classList.remove('is-leaving');
          slides[i].classList.remove('is-entering');
          locked = false;
        }, reduce ? 0 : 700);
      }

      function setActive(next) {
        var normalized = (next + slides.length) % slides.length;
        if (locked || normalized === i) return;
        locked = true;
        loadSlideImage(normalized).then(function () {
          activateSlide(normalized);
        });
      }

      function go(n) {
        setActive(n);
      }

      function start() {
        stop();
        updateLiveRegion();
        if (userPaused || hoverPaused || focusPaused || document.hidden || slides.length < 2) return;
        timer = window.setInterval(function () {
          go(i + 1);
        }, interval);
      }

      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      var nextBtn = root.querySelector('.jw-hero__nav--next');
      var prevBtn = root.querySelector('.jw-hero__nav--prev');
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          go(i + 1);
          start();
        });
      }
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          go(i - 1);
          start();
        });
      }
      if (toggle) {
        toggle.addEventListener('click', function () {
          userPaused = !userPaused;
          updateToggle();
          if (userPaused) {
            stop();
          } else {
            // An explicit Play action must take precedence over the focus/hover
            // pause that was created while the customer reached this control.
            focusPaused = false;
            hoverPaused = false;
            start();
          }
        });
      }
      dots.forEach(function (d) {
        d.addEventListener('click', function () {
          go(parseInt(d.getAttribute('data-index'), 10));
          start();
        });
        d.addEventListener('keydown', function (event) {
          var nextIndex = i;
          if (event.key === 'ArrowRight') nextIndex = i + 1;
          if (event.key === 'ArrowLeft') nextIndex = i - 1;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = slides.length - 1;
          if (nextIndex === i) return;
          event.preventDefault();
          var normalizedIndex = (nextIndex + slides.length) % slides.length;
          go(normalizedIndex);
          if (dots[normalizedIndex]) dots[normalizedIndex].focus();
          start();
        });
      });

      // Pause on hover
      root.addEventListener('mouseenter', function () {
        hoverPaused = true;
        stop();
        updateLiveRegion();
      });
      root.addEventListener('mouseleave', function () {
        hoverPaused = false;
        start();
      });
      root.addEventListener('focusin', function () {
        focusPaused = true;
        stop();
        updateLiveRegion();
      });
      root.addEventListener('focusout', function (event) {
        if (!root.contains(event.relatedTarget)) {
          focusPaused = false;
          start();
        }
      });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          stop();
          updateLiveRegion();
        }
        else start();
      });

      if (reduceMotionQuery) {
        var handleMotionPreference = function (event) {
          reduce = event.matches;
          if (reduce) {
            userPaused = true;
            stop();
          }
          updateToggle();
        };
        if (reduceMotionQuery.addEventListener) {
          reduceMotionQuery.addEventListener('change', handleMotionPreference);
        } else if (reduceMotionQuery.addListener) {
          reduceMotionQuery.addListener(handleMotionPreference);
        }
      }

      // Soft parallax on active media
      if (!reduce) {
        root.addEventListener(
          'pointermove',
          function (e) {
            if (reduce) return;
            var active = root.querySelector('.jw-hero__slide.is-active .jw-hero__media img');
            if (!active) return;
            var rect = root.getBoundingClientRect();
            var x = (e.clientX - rect.left) / rect.width - 0.5;
            var y = (e.clientY - rect.top) / rect.height - 0.5;
            active.style.transform =
              'scale(1.06) translate(' + (x * 12).toFixed(2) + 'px,' + (y * 8).toFixed(2) + 'px)';
          },
          { passive: true }
        );
        root.addEventListener('pointerleave', function () {
          var active = root.querySelector('.jw-hero__slide.is-active .jw-hero__media img');
          if (active) active.style.transform = '';
        });
      }

      // Mark hero content for entrance
      var content = root.querySelector('.jw-hero__slide.is-active .jw-hero__content');
      if (content) {
        content.classList.add('jw-hero__content--ready');
      }

      updateAccessibility();
      updateToggle();
      start();
    });
  }

  function magneticButtons() {
    if (reduce || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('.jw-hero__btn, .jw-promo__btn, .jw-story__button').forEach(function (btn) {
      if (btn.dataset.jwMagnetic) return;
      btn.dataset.jwMagnetic = '1';
      btn.addEventListener('pointermove', function (e) {
        if (reduce) return;
        var r = btn.getBoundingClientRect();
        var x = e.clientX - r.left - r.width / 2;
        var y = e.clientY - r.top - r.height / 2;
        btn.style.transform = 'translate(' + x * 0.12 + 'px,' + y * 0.18 + 'px)';
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.transform = '';
      });
    });
  }

  ready(function () {
    document.documentElement.classList.add('jw-motion');
    if (reduce) document.documentElement.classList.add('jw-motion-reduce');
    markSections();
    observeReveals();
    enhanceHero();
    magneticButtons();
  });
})();
